import styled from 'styled-components';
import { ScButton } from '../../components/buttons/styled';
import { ScCTAButton } from '../../components/CTAButton/styled';
import { ScCopyCode } from '../../components/DashedCopyCode/styled';
import { ScLogo } from '../../components/Logo/styled';
import { ScTermsAndConditions } from '../../components/TermsAndConditions/styled';

// Pass literal Figma px (1280x720, portrait 360x720); --fluix-px scales them.
// --fluix-px is a length: unit division (100vw / 1px) is unsupported on iOS 17 Safari and breaks every fpx().
export const fpx = (px: number) => `calc(${px} * var(--fluix-px))`;

export const ScRoot = styled.div<{ bgImage: string }>`
  position: relative;
  overflow-x: hidden;
  overflow-y: visible;
  width: 100%;
  min-height: 100vh;
  min-height: 100dvh;
  height: auto;

  background-color: ${({ theme }) => theme.blackColor};
  background-image: url(${({ bgImage }) => bgImage});
  background-repeat: no-repeat;
  background-position: top left;
  background-size: cover;

  --fluix-px: min(calc(100vw / 1280), calc(100vh / 720));

  @media (orientation: portrait) {
    background-position: center top;
    --fluix-px: min(calc(100vw / 360), calc(100vh / 720));

    &::before {
      content: "";
      position: absolute;
      z-index: 4;
      top: 0;
      left: 0;
      width: 100%;
      height: ${fpx(116)};
      opacity: 0.8;
      background: linear-gradient(180deg, #000 42.5%, rgba(0, 0, 0, 0) 100%);
      pointer-events: none;
    }
  }
`;

export const ScDecorTopLeft = styled.img`
  position: absolute;
  z-index: 1;
  top: ${fpx(-63)};
  left: ${fpx(-103)};
  width: ${fpx(248)};
  height: ${fpx(282)};
  pointer-events: none;

  @media (orientation: portrait) {
    display: none;
  }
`;

export const ScMainImg = styled.img`
  z-index: 2;
  flex-shrink: 0;
  width: ${fpx(793)};
  height: 100dvh;
  min-height: 100dvh;
  object-fit: cover;
  object-position: bottom center;
  overflow: visible;
  margin-left: ${fpx(-13)};

  @media (orientation: landscape) {
    align-self: flex-end;
  }

  @media (orientation: portrait) {
    display: none;
  }
`;

export const ScMainImgMob = styled.div`
  display: none;

  @media (orientation: portrait) {
    position: relative;
    z-index: 1;
    display: block;
    flex-shrink: 0;
    overflow: hidden;
    width: ${fpx(360)};
    height: ${fpx(327)};
    margin-top: ${fpx(-32)};
    margin-bottom: ${fpx(-29)};
    pointer-events: none;
  }
`;

export const ScMainImgMobLight = styled.img`
  position: absolute;
  top: ${fpx(49)};
  left: ${fpx(-66.06)};
  width: ${fpx(483.306)};
  height: ${fpx(331.188)};
  aspect-ratio: 251 / 172;
  object-fit: cover;
  mask-image: linear-gradient(180deg, #000 66.7%, transparent 78.5%);
`;

export const ScMainImgMobCharacter = styled.img`
  position: relative;
  display: block;
  width: 100%;
  height: 100%;
`;

export const ScDecorMoney = styled.img`
  position: absolute;
  z-index: 3;
  bottom: 0;
  right: 0;
  width: ${fpx(380)};
  height: ${fpx(235)};
  pointer-events: none;

  @media (orientation: portrait) {
    display: none;
  }
`;

export const ScDecorCoins = styled.img`
  position: absolute;
  z-index: 6;
  bottom: 0;
  right: 0;
  width: ${fpx(258)};
  height: ${fpx(273)};
  pointer-events: none;

  @media (orientation: portrait) {
    display: none;
  }
`;

export const ScAwardsShadow = styled.div`
  position: absolute;
  z-index: 4;
  bottom: 0;
  left: 0;
  width: 100vw;
  height: ${fpx(116)};
  opacity: 0.8;
  background: linear-gradient(0deg, #000 42.5%, rgba(0, 0, 0, 0) 100%);

  @media (orientation: portrait) {
    width: 100%;
    height: ${fpx(156)};
    opacity: 1;
  }
`;

export const ScAwardsRow = styled.div`
  position: absolute;
  z-index: 7;
  bottom: ${fpx(30)};
  left: 50%;
  transform: translateX(-50%);
  display: flex;
  align-items: center;
  justify-content: center;
  width: ${fpx(890)};
  gap: ${fpx(40)};

  img {
    width: auto;
    height: ${fpx(32)};
  }

  @media (orientation: portrait) {
    bottom: ${fpx(10)};
    flex-wrap: wrap;
    width: ${fpx(360)};
    gap: ${fpx(12)};

    img {
      height: ${fpx(22)};
    }
  }
`;

export const ScLayout = styled.div`
  position: relative;
  display: flex;
  align-items: flex-start;
  justify-content: center;
  width: 100%;
  min-height: 100dvh;
  height: auto;

  @media (orientation: portrait) {
    flex-direction: column;
    align-items: center;
    justify-content: flex-start;
  }
`;
export const ScContent = styled.div`
  position: relative;
  z-index: 7;
  flex-shrink: 0;
  display: flex;
  justify-content: center;
  align-items: flex-start;
  height: auto;

  @media (orientation: landscape) {
    padding-top: ${fpx(115)};
    padding-bottom: ${fpx(115)};
  }

  @media (orientation: portrait) {
    width: ${fpx(320)};
    padding-top: ${fpx(20)};
    padding-bottom: ${fpx(92)};
  }
`;

export const ScContentInner = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: ${fpx(26)};
  width: ${fpx(432)};
  padding-top: ${fpx(1)};
  margin-left: ${fpx(68)};

  ${ScCopyCode} {
    gap: ${fpx(12)};
    height: ${fpx(34)};
    padding-left: ${fpx(20)};
    padding-right: ${fpx(4)};
    border-radius: 100px;
    border: 1px dashed rgba(255, 255, 255, 0.5);
    background: rgba(81, 20, 13, 0.2);

    & > .copy-code > p {
      display: flex;
      justify-content: center;
      align-items: center;
      font-family: "Work Sans", sans-serif;
      border: none;
      line-height: normal;
      font-size: ${fpx(12)};
      font-weight: 800;
      letter-spacing: 3px;
      height: auto;
      padding: 0;
    }
    ${ScButton} {
      border-radius: 100px;
      height: ${fpx(26)};
      min-width: ${fpx(93)};
      padding-left: ${fpx(6)};
      padding-top: 0;
      padding-right: ${fpx(6)};
      padding-bottom: 0;
      margin-left: 0;
      white-space: nowrap;
      display: flex;
      justify-content: center;
      align-items: center;
      background:
        linear-gradient(90deg, #ffe47a 0%, #f5b932 46.63%, #e89217 100%),
        linear-gradient(0deg, #e99419 0%, #e99419 100%),
        linear-gradient(90deg, #ffd84a 0%, #f5a623 100%);
    }
    .text {
      color: #4a1708;
      font-family: "Work Sans", sans-serif;
      font-size: ${fpx(12)};
      font-style: normal;
      font-weight: 700;
      line-height: ${fpx(16)};
      letter-spacing: 0;
      text-box: trim-both cap alphabetic;
    }
  }

  ${ScCTAButton} {
    font-size: ${fpx(16)};
    line-height: ${fpx(24)};
  }

  @media (orientation: portrait) {
    gap: ${fpx(12)};
    width: ${fpx(320)};
    padding-top: 0;
    margin-left: 0;

    ${ScLogo} {
      margin-bottom: ${fpx(8)};

      img {
        width: ${fpx(114)};
        height: ${fpx(30)};
      }
    }
  }
`;

export const ScOfferTitle = styled.h2`
  margin: 0;
  width: 100%;
  color: ${({ theme }) => theme.yellowColor};
  font-family: "Work Sans", sans-serif;
  font-size: ${fpx(22)};
  font-weight: 700;
  line-height: ${fpx(24)};
  letter-spacing: 0.44px;
  text-align: center;
  text-transform: uppercase;
  margin-bottom: ${fpx(-14)};

  @media (orientation: portrait) {
    font-size: ${fpx(18)};
    line-height: ${fpx(21)};
    letter-spacing: 0.36px;
    margin-bottom: ${fpx(-6)};
  }
`;

export const ScOfferPrize = styled.h1`
  margin: 0;
  width: 100%;
  color: ${({ theme }) => theme.whiteColor};
  font-family: "Work Sans", sans-serif;
  font-size: ${fpx(40)};
  letter-spacing: 1.2px;
  font-weight: 700;
  line-height: ${fpx(47)};
  text-align: center;
  text-transform: uppercase;

  &.lang-fr-CA,
  &.lang-fr-CH {
    font-size: ${fpx(32)};
  }

  @media (orientation: portrait) {
    font-size: ${fpx(32)};
    line-height: ${fpx(38)};
    letter-spacing: 0.96px;
    &.lang-fr-CA,
    &.lang-fr-CH {
      font-size: ${fpx(22)};
    }
  }
`;

export const ScCtaTermsWrap = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: ${fpx(16)};

  ${ScCTAButton} {
    height: ${fpx(48)};
    width: ${fpx(320)};
  }

  ${ScTermsAndConditions} {
    font-size: ${fpx(14)};
    line-height: ${fpx(20)};

    img {
      width: ${fpx(16)};
    }
  }

  @media (orientation: portrait) {
    gap: ${fpx(8)};

    ${ScTermsAndConditions} {
      font-size: ${fpx(12)};
    }
  }
`;

export const ScTermsWrap = styled.div`
  color: #fff;

  ${ScTermsAndConditions} .terms {
    transition: grid-template-rows 0.25s ease;

    &-content {
      transition: opacity 0.25s ease;
    }
  }
`;
